import type { AppRouter } from '@sitzmark/api';
import type { TRPCClient } from '@trpc/client';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import { apiOriginFor } from './api-origin.js';

/**
 * The API client.
 *
 * Tenant comes from the subdomain, matching how the server resolves it
 * (SCOPE §12.3). Locally there is no wildcard DNS, so it falls back to an env
 * override — the same asymmetry the server has, and equally confined to dev.
 */
export function tenantSlug(): string {
  const labels = window.location.hostname.split('.');
  if (labels.length > 1 && labels[0] !== undefined && labels[0] !== 'www') return labels[0];
  return import.meta.env.VITE_TENANT ?? 'sitzmark';
}

// On a `shekko.app` tenant hostname the API address is derived, so one static
// bundle serves every tenant; everywhere else the compiled URL still decides
// (see api-origin.ts). Guarded because this runs at module scope, and the
// unit suite imports screens under node, where there is no `window` — an
// empty hostname takes the same fallback the tests always ran against.
const baseUrl = apiOriginFor(
  typeof window === 'undefined' ? '' : window.location.hostname,
  import.meta.env.VITE_API_URL,
);

// Annotated explicitly: the inferred type reaches into the API package's
// internal modules, which TypeScript cannot name from here (TS2742).
export const api: TRPCClient<AppRouter> = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${baseUrl}/trpc`,
      // The session rides on an httpOnly cookie, so credentials must go out
      // cross-origin during development.
      fetch: (input, init) => fetch(input, { ...init, credentials: 'include' }),
      headers: () => ({ 'x-sitzmark-tenant': tenantSlug() }),
    }),
  ],
});

/** Pull the human-readable message out of a tRPC error without leaking a stack. */
/**
 * A refusal, in a sentence.
 *
 * tRPC serialises a Zod failure by putting the validator's own JSON in
 * `message`, so a screen that renders it verbatim shows the operator
 * `[{"origin":"array","code":"too_small","minimum":1,…}]`. That is not an error
 * message, it is a stack trace with the punctuation of one — and it appeared on
 * the reports screen the first time somebody switched every grouping level off.
 *
 * The field errors the server already attaches are the readable version, so
 * they are preferred; the JSON is only ever a fallback of last resort, and even
 * then it is replaced rather than shown.
 */
export function errorMessage(error: unknown): string {
  const validation = (error as { data?: { validation?: Record<string, string[]> | null } })?.data
    ?.validation;

  if (validation != null) {
    const sentences = Object.values(validation)
      .flat()
      .filter((entry) => entry.trim() !== '');
    if (sentences.length > 0) return sentences.join('. ');
  }

  if (error instanceof Error && error.message !== '') {
    // A bare JSON array is the validator talking to itself, not to a person.
    if (/^\s*[[{]/.test(error.message)) return 'That request was not valid. Try again.';
    return error.message;
  }

  return 'Something went wrong. Try again.';
}
